import { useMemo } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import Markdown from "react-native-markdown-display";

import { fontFamily, radius, space, textStyle } from "@passfirst/ui";

import { Button } from "../../components/button";
import { type Colors, useColors } from "../../theme";
import { makeTutorMarkdownStyles } from "../tutor-markdown-styles";
import type { TutorMessage } from "../types";

interface Props {
  messages: TutorMessage[];
  input: string;
  setInput: (v: string) => void;
  sending: boolean;
  error: string | null;
  userTurnCount: number;
  capped: boolean;
  onSend: (textOverride?: string) => Promise<void>;
  /** Capped CTA → orchestrator advances to the next question. */
  onAdvance: () => void;
}

const QUICK_REPLIES = [
  "I see it now",
  "Why is the right answer right?",
  "Walk me through it",
];

// Socratic tutor panel — only on wrong answers. Bubbles render as
// markdown for assistant messages, plain text for user messages. The
// 4-turn cap swaps the input row for "I think I get it →" once
// `userTurnCount` reaches the cap. Quick-reply chips appear only on
// the kickoff (when no user turn has landed yet).
export function TutorPanel({
  messages,
  input,
  setInput,
  sending,
  error,
  userTurnCount,
  capped,
  onSend,
  onAdvance,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const markdownStyles = useMemo(
    () => makeTutorMarkdownStyles(colors),
    [colors],
  );
  const showChips = !capped && userTurnCount === 0;
  return (
    <View style={styles.tutorPanel}>
      <Text style={styles.tutorPanelHeader}>Let&apos;s talk through it</Text>

      {messages.map((m, i) => (
        <View
          key={i}
          style={[
            styles.tutorBubble,
            m.role === "assistant"
              ? styles.tutorBubbleAssistant
              : styles.tutorBubbleUser,
          ]}
        >
          {m.role === "assistant" ? (
            <View accessibilityLiveRegion="polite">
              <Markdown style={markdownStyles}>{m.content}</Markdown>
            </View>
          ) : (
            <Text style={styles.tutorBubbleText}>{m.content}</Text>
          )}
        </View>
      ))}

      {sending ? (
        <View
          style={[
            styles.tutorBubble,
            styles.tutorBubbleAssistant,
            styles.tutorBubbleThinking,
          ]}
          accessibilityLiveRegion="polite"
          accessibilityLabel="Tutor is thinking"
        >
          <ActivityIndicator size="small" color={colors.accentSoftText} />
          <Text style={styles.tutorBubbleText}>Thinking…</Text>
        </View>
      ) : null}

      {!capped ? (
        <>
          {showChips ? (
            <View style={styles.tutorChipsRow}>
              {QUICK_REPLIES.map((chip) => (
                <Pressable
                  key={chip}
                  onPress={() => void onSend(chip)}
                  disabled={sending}
                  accessibilityRole="button"
                  accessibilityLabel={`Quick reply: ${chip}`}
                  style={[styles.tutorChip, sending && styles.disabled]}
                >
                  <Text style={styles.tutorChipText}>{chip}</Text>
                </Pressable>
              ))}
            </View>
          ) : null}

          <View style={styles.tutorInputRow}>
            <TextInput
              style={styles.tutorInput}
              value={input}
              onChangeText={setInput}
              placeholder="Type your reasoning…"
              placeholderTextColor={colors.textMuted}
              multiline
              editable={!sending}
              returnKeyType="send"
              onSubmitEditing={() => void onSend()}
              accessibilityLabel="Reply to the tutor"
            />
            <Button
              label="Send"
              onPress={() => void onSend()}
              disabled={input.trim().length === 0}
              busy={sending}
              accessibilityLabel="Send reply to tutor"
            />
          </View>
        </>
      ) : null}

      {error ? (
        <Text style={styles.error} accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}

      {capped ? (
        <Button
          label="I think I get it"
          onPress={onAdvance}
          fullWidth
          accessibilityLabel="I get it, next question"
        />
      ) : (
        <Button
          label="Skip — next question"
          variant="secondary"
          onPress={onAdvance}
          fullWidth
          accessibilityLabel="Skip tutor and go to next question"
        />
      )}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    tutorPanel: {
      gap: space[3],
      padding: space[4],
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.surfacePicked,
      backgroundColor: colors.surfaceTutor,
    },
    tutorPanelHeader: {
      fontFamily: fontFamily.semiBold,
      fontSize: 14,
      color: colors.accentSoftText,
      marginBottom: space[1],
    },
    tutorBubble: {
      paddingVertical: space[3],
      paddingHorizontal: space[4],
      borderRadius: radius.md,
      maxWidth: "85%",
    },
    tutorBubbleAssistant: {
      backgroundColor: colors.surfaceMuted,
      alignSelf: "flex-start",
    },
    tutorBubbleUser: {
      backgroundColor: colors.surfacePicked,
      alignSelf: "flex-end",
    },
    tutorBubbleText: { ...textStyle.body, color: colors.textPrimary },
    tutorBubbleThinking: {
      flexDirection: "row",
      alignItems: "center",
      gap: space[2],
    },
    tutorChipsRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 7,
      marginBottom: space[3],
    },
    tutorChip: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.pill,
      paddingVertical: 8,
      paddingHorizontal: 13,
    },
    tutorChipText: {
      fontFamily: fontFamily.regular,
      fontSize: 13,
      color: colors.textSecondary,
    },
    tutorInputRow: {
      flexDirection: "row",
      alignItems: "flex-end",
      gap: space[2],
    },
    tutorInput: {
      flex: 1,
      minHeight: 44,
      maxHeight: 140,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingVertical: space[3],
      paddingHorizontal: space[4],
      fontSize: 15,
      color: colors.textPrimary,
      backgroundColor: colors.surface,
      fontFamily: fontFamily.regular,
    },
    disabled: { opacity: 0.5 },
    error: {
      ...textStyle.bodySmall,
      color: colors.error,
      textAlign: "center",
    },
  });
}
